import React, { useEffect, useState } from 'react';
import BankIcon from 'assets/icons/tandempay/bank-circle.svg';
import PageActionsFooter from '../PageActionsFooter';
import ModalPage from '../ModalPage';
import PageContent from '../PageContent';
import { useUserContext } from 'UserContext';
import { useStripe } from '@stripe/react-stripe-js';
import { SetupIntent } from '@stripe/stripe-js';
import { client } from 'shared';
import { Grid, styled } from '@mui/material';
import { PrimaryButton, Text } from 'library';

interface ConnectBankAccountPageProps {
    goToSuccess: () => void;
    goBack: () => void;
    showHeaderAndFooter: boolean;
}

export default function ConnectBankAccountPage({
    goToSuccess,
    goBack,
    showHeaderAndFooter,
}: ConnectBankAccountPageProps) {
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [setupIntent, setSetupIntent] = useState<SetupIntent>();
    const [clientSecret, setClientSecret] = useState('');

    const { user } = useUserContext();
    const stripeHook = useStripe();

    useEffect(() => {
        startCollectPayment();
    }, []);

    async function startCollectPayment() {
        let customer_id = user?.customer_id;
        if (!customer_id) {
            const customer_response = await client('payment/api/pay-setup/create-customer/', { method: 'POST' });
            customer_id = customer_response.customer_id;
        }

        const intentResponse = await client('payment/api/pay-setup/start-setup/', {
            body: { customer_id: customer_id },
        });
        setClientSecret(intentResponse.client_secret);
        stripeHook
            ?.collectBankAccountForSetup({
                clientSecret: intentResponse.client_secret,
                params: {
                    payment_method_type: 'us_bank_account',
                    payment_method_data: {
                        billing_details: {
                            name: `${user?.first_name} ${user?.last_name}`,
                            email: user?.email,
                        },
                    },
                },
                expand: ['payment_method'],
            })
            .then(({ setupIntent, error }) => {
                if (error) {
                    client('payment/api/setup-events/', {
                        body: { data: JSON.stringify(error), processor: 'Stripe' },
                    }).catch(() => {});
                } else if (setupIntent.status === 'requires_payment_method') {
                    goBack();
                } else if (setupIntent.status === 'requires_confirmation') {
                    setShowConfirmation(true);
                    setSetupIntent(setupIntent);
                }
            });
    }

    function savePaymentMethod(paymentMethodId: string, status: string, requiresAction: boolean) {
        client('payment/api/pay-setup/', {
            body: {
                payment_method: paymentMethodId,
                status: status,
                requires_action: requiresAction,
            },
        })
            .catch()
            .finally(() => goToSuccess());
    }

    async function confirmBankAccount() {
        stripeHook?.confirmUsBankAccountSetup(clientSecret).then(({ setupIntent, error }) => {
            const payMethodId =
                setupIntent && typeof setupIntent.payment_method === 'string' ? setupIntent.payment_method : '';
            if (error) {
                client('payment/api/setup-events/', {
                    body: { data: JSON.stringify(error), processor: 'Stripe' },
                }).catch(() => {});
            } else if (setupIntent.status === 'requires_payment_method') {
                goBack();
            } else if (setupIntent.status === 'succeeded') {
                savePaymentMethod(payMethodId, setupIntent.status, false);
            } else if (setupIntent.next_action?.type === 'verify_with_microdeposits') {
                savePaymentMethod(payMethodId, setupIntent.status, true);
            }
        });
    }

    function displayConfirmDetails() {
        if (!showConfirmation || typeof setupIntent?.payment_method === 'string') {
            return;
        }

        return (
            <ConfirmContainer>
                <Text textStyle={{ margin: '5px 0' }}>
                    <b>Acct. #:</b> ****{setupIntent?.payment_method?.us_bank_account?.last4}
                </Text>
                <Text textStyle={{ margin: '5px 0' }}>
                    <b>Routing. #:</b> {setupIntent?.payment_method?.us_bank_account?.routing_number}
                </Text>
                <PrimaryButton onClick={confirmBankAccount} buttonStyle={{ width: 'fit-content', marginTop: 5 }}>
                    Confirm account details
                </PrimaryButton>
            </ConfirmContainer>
        );
    }

    return (
        <ModalPage
            iconSource={<BankIcon />}
            headerText="Connect Bank Account"
            subtitleText="Enter and confirm your bank account details."
            showHeader={showHeaderAndFooter}
        >
            <PageContent>{displayConfirmDetails()}</PageContent>
            {showHeaderAndFooter && <PageActionsFooter goBack={goBack} />}
        </ModalPage>
    );
}

const ConfirmContainer = styled(Grid)({
    padding: 40,
    paddingTop: 0,
});
